import { useState } from "react";
import { Save, MessageCircle, Send } from "lucide-react";
import { memberApi, formatApiError } from "../api";
import { useAuth } from "../AuthContext";

const CLASS_TYPES = ["Foundation Reformer", "Active Reformer", "Core Sculpt", "Strength Reformer", "Power Reformer", "Flow & Stretch"];

export default function Profile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    medical_conditions: user?.medical_conditions || "",
    emergency_contact_name: user?.emergency_contact_name || "",
    emergency_contact_phone: user?.emergency_contact_phone || "",
    preferred_classes: user?.preferred_classes || [],
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  // Contact studio mini-form
  const [contact, setContact] = useState({ subject: "", message: "" });
  const [contactSent, setContactSent] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleClass = (name) => {
    setForm((f) => ({
      ...f,
      preferred_classes: f.preferred_classes.includes(name)
        ? f.preferred_classes.filter((c) => c !== name)
        : [...f.preferred_classes, name],
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg("");
    setErr("");
    try {
      const { data } = await memberApi.patch("/api/members/me", form);
      setUser(data);
      setMsg("Profile updated ✓");
      setTimeout(() => setMsg(""), 3000);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const sendContact = async (e) => {
    e.preventDefault();
    try {
      await memberApi.post("/api/members/me/contact", contact);
      setContactSent(true);
      setContact({ subject: "", message: "" });
      setTimeout(() => setContactSent(false), 4000);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-8" data-testid="member-profile-page">
      <div>
        <h2 className="text-2xl font-light text-[#E8DCC4]">My Profile</h2>
        <p className="text-[#E8DCC4]/60 text-sm mt-1">{user?.email}</p>
      </div>

      {msg && <div data-testid="profile-success" className="bg-[#C97654]/20 border border-[#C97654] rounded-lg px-4 py-2 text-[#FFB89A] text-sm">{msg}</div>}
      {err && <div className="bg-[#8B2635]/20 border border-[#8B2635] rounded-lg px-4 py-2 text-[#FFB89A] text-sm">{err}</div>}

      <form onSubmit={save} className="space-y-4 bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-5">
        <h3 className="text-[#C97654] text-sm uppercase tracking-[0.2em] font-bold">Personal Details</h3>
        <Field label="Full Name" value={form.name} onChange={upd("name")} testid="profile-name" />
        <Field label="Phone" value={form.phone} onChange={upd("phone")} testid="profile-phone" />

        <h3 className="text-[#C97654] text-sm uppercase tracking-[0.2em] font-bold pt-4">Health &amp; Safety</h3>
        <TextArea label="Medical Conditions / Injuries" value={form.medical_conditions} onChange={upd("medical_conditions")} testid="profile-medical"
          placeholder="Any conditions our instructors should know about?" />

        <h3 className="text-[#C97654] text-sm uppercase tracking-[0.2em] font-bold pt-4">Emergency Contact</h3>
        <Field label="Contact Name" value={form.emergency_contact_name} onChange={upd("emergency_contact_name")} testid="profile-emergency-name" />
        <Field label="Contact Phone" value={form.emergency_contact_phone} onChange={upd("emergency_contact_phone")} testid="profile-emergency-phone" />

        <h3 className="text-[#C97654] text-sm uppercase tracking-[0.2em] font-bold pt-4">Preferred Class Types</h3>
        <div className="flex flex-wrap gap-2">
          {CLASS_TYPES.map((c) => {
            const on = form.preferred_classes.includes(c);
            return (
              <button
                key={c}
                type="button"
                onClick={() => toggleClass(c)}
                data-testid={`class-chip-${c.replace(/\s+/g, "-").toLowerCase()}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  on
                    ? "bg-[#C97654] text-white shadow"
                    : "bg-[#0a0a0a] border border-[#2a2a2a] text-[#E8DCC4]/60 hover:border-[#C97654]/50"
                }`}
              >
                {c}
              </button>
            );
          })}
        </div>

        <button
          type="submit"
          disabled={saving}
          data-testid="profile-save-btn"
          className="w-full bg-gradient-to-r from-[#C97654] to-[#FF9472] text-white py-3 rounded-lg font-bold shadow-md flex items-center justify-center gap-2 disabled:opacity-50 mt-4"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </form>

      {/* Contact the studio */}
      <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <MessageCircle className="w-5 h-5 text-[#C97654]" />
          <h3 className="text-[#E8DCC4] font-medium">Contact the Studio</h3>
        </div>
        <p className="text-[#E8DCC4]/50 text-xs mb-4">Send us a message — we'll reply to your email.</p>

        {contactSent ? (
          <div data-testid="contact-success" className="bg-[#C97654]/20 border border-[#C97654] rounded-lg px-4 py-3 text-center text-[#FFB89A] text-sm">
            Message sent — we'll be in touch soon ✓
          </div>
        ) : (
          <form onSubmit={sendContact} className="space-y-3">
            <input
              required
              type="text"
              value={contact.subject}
              onChange={(e) => setContact({ ...contact, subject: e.target.value })}
              placeholder="Subject"
              data-testid="contact-subject"
              className="w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#C97654] rounded-lg px-4 py-2.5 text-sm text-[#E8DCC4] placeholder-[#E8DCC4]/30 outline-none"
            />
            <textarea
              required
              rows={4}
              value={contact.message}
              onChange={(e) => setContact({ ...contact, message: e.target.value })}
              placeholder="What would you like to ask?"
              data-testid="contact-message"
              className="w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#C97654] rounded-lg px-4 py-2.5 text-sm text-[#E8DCC4] placeholder-[#E8DCC4]/30 outline-none resize-none"
            />
            <button
              type="submit"
              data-testid="contact-send-btn"
              className="w-full bg-[#8B2635] hover:bg-[#a03042] text-white py-2.5 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition-colors"
            >
              <Send className="w-4 h-4" />
              Send Message
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <div>
      <label className="block text-[#E8DCC4]/70 text-xs mb-1.5 font-medium">{label}</label>
      <input data-testid={testid} className="w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#C97654] rounded-lg px-4 py-2.5 text-sm text-[#E8DCC4] outline-none" {...rest} />
    </div>
  );
}

function TextArea({ label, testid, ...rest }) {
  return (
    <div>
      <label className="block text-[#E8DCC4]/70 text-xs mb-1.5 font-medium">{label}</label>
      <textarea rows={3} data-testid={testid} className="w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#C97654] rounded-lg px-4 py-2.5 text-sm text-[#E8DCC4] outline-none resize-none" {...rest} />
    </div>
  );
}
